import React, { useEffect, useState, useRef, useMemo } from 'react';
import api from '../services/api';
import { Calendar, Filter, ZoomIn, ZoomOut, ChevronLeft, ChevronRight,
         CheckCircle, Circle, Clock, AlertTriangle, Shield, FolderKanban } from 'lucide-react';

const STATUS_COLORS = {
  pending: '#64748B', in_progress: '#3B82F6', completed: '#10B981', blocked: '#EF4444',
};
const STATUS_LABELS = {
  pending: 'Pendiente', in_progress: 'En progreso', completed: 'Completada', blocked: 'Bloqueada',
};
const STATUS_ICONS = {
  pending: Circle, in_progress: Clock, completed: CheckCircle, blocked: AlertTriangle,
};

export default function GanttView() {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [tasks, setTasks] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [zoom, setZoom] = useState(1); // 0.5=month, 1=week, 2=day
  const [scrollOffset, setScrollOffset] = useState(0);
  const timelineRef = useRef(null);

  // Load projects
  useEffect(() => {
    api.get('/projects').then(r => {
      const data = r.data.data || r.data || [];
      setProjects(data);
      const active = data.find(p => p.status === 'in_progress');
      if (active) setSelectedProject(active.id);
      else if (data.length > 0) setSelectedProject(data[0].id);
    }).catch(() => {});
  }, []);

  // Load tasks + milestones for selected project
  useEffect(() => {
    if (!selectedProject) { setLoading(false); return; }
    setLoading(true);
    Promise.all([
      api.get(`/projects/${selectedProject}`),
      api.get('/tasks', { params: { project_id: selectedProject } }),
      api.get('/milestones', { params: { project_id: selectedProject } }),
    ]).then(([pRes, tRes, mRes]) => {
      setProject(pRes.data);
      setTasks(tRes.data || []);
      setMilestones(mRes.data || []);
    }).catch(console.error)
      .finally(() => setLoading(false));
  }, [selectedProject]);

  // Calculate timeline bounds
  const { startDate, endDate, totalDays, dayWidth, rows } = useMemo(() => {
    const allDates = [];
    const rows = [];

    tasks.forEach(t => {
      const start = t.planned_start || t.actual_start || t.created_at;
      const end = t.planned_end || t.actual_end || t.completed_at;
      if (start) allDates.push(new Date(start));
      if (end) allDates.push(new Date(end));
      rows.push({ type: 'task', data: t, start, end });
    });

    milestones.forEach(m => {
      const date = m.deadline_date || m.planned_date || m.actual_date;
      if (date) allDates.push(new Date(date));
      rows.push({ type: 'milestone', data: m, date });
    });

    if (project?.start_date) allDates.push(new Date(project.start_date));
    if (project?.estimated_end_date) allDates.push(new Date(project.estimated_end_date));

    if (allDates.length === 0) {
      const now = new Date();
      allDates.push(new Date(now.getTime() - 30 * 86400000));
      allDates.push(new Date(now.getTime() + 90 * 86400000));
    }

    const minDate = new Date(Math.min(...allDates.map(d => d.getTime())));
    const maxDate = new Date(Math.max(...allDates.map(d => d.getTime())));

    // Add padding
    const startDate = new Date(minDate.getTime() - 7 * 86400000);
    const endDate = new Date(maxDate.getTime() + 14 * 86400000);
    const totalDays = Math.ceil((endDate - startDate) / 86400000);

    const dayWidth = zoom === 0.5 ? 8 : zoom === 1 ? 20 : 40;

    return { startDate, endDate, totalDays, dayWidth, rows };
  }, [tasks, milestones, project, zoom]);

  const getX = (dateStr) => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    const diff = (d - startDate) / 86400000;
    return Math.round(diff * dayWidth);
  };

  const today = new Date();
  const todayX = getX(today.toISOString());

  // Generate month headers
  const months = useMemo(() => {
    const result = [];
    const d = new Date(startDate);
    d.setDate(1);
    while (d <= endDate) {
      const x = getX(d.toISOString());
      const nextMonth = new Date(d.getFullYear(), d.getMonth() + 1, 1);
      const width = getX(nextMonth.toISOString()) - x;
      result.push({
        label: d.toLocaleDateString('es-ES', { month: 'short', year: '2-digit' }),
        x, width,
      });
      d.setMonth(d.getMonth() + 1);
    }
    return result;
  }, [startDate, endDate, dayWidth]);

  // Generate week lines
  const weekLines = useMemo(() => {
    const lines = [];
    const d = new Date(startDate);
    // Align to Monday
    d.setDate(d.getDate() + ((8 - d.getDay()) % 7));
    while (d <= endDate) {
      lines.push(getX(d.toISOString()));
      d.setDate(d.getDate() + 7);
    }
    return lines;
  }, [startDate, endDate, dayWidth]);

  const scrollTo = (direction) => {
    if (timelineRef.current) {
      const delta = direction === 'left' ? -300 : 300;
      timelineRef.current.scrollBy({ left: delta, behavior: 'smooth' });
    }
  };

  const scrollToToday = () => {
    if (timelineRef.current && todayX) {
      timelineRef.current.scrollTo({
        left: todayX - timelineRef.current.clientWidth / 2,
        behavior: 'smooth',
      });
    }
  };

  const ROW_HEIGHT = 38;
  const HEADER_HEIGHT = 50;
  const SIDEBAR_WIDTH = 280;
  const timelineWidth = totalDays * dayWidth;

  return (
    <div className="gantt-page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1>Gantt de Obra</h1>
          <p className="subtitle">Timeline visual de tareas e hitos</p>
        </div>
      </div>

      {/* Controls */}
      <div className="gantt-controls">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Filter size={16} style={{ color: 'var(--text-muted)' }} />
          <select className="filter-select" value={selectedProject}
            onChange={e => setSelectedProject(e.target.value)}>
            <option value="">Seleccionar proyecto</option>
            {projects.map(p => <option key={p.id} value={p.id}>{p.code} - {p.name}</option>)}
          </select>
        </div>

        <div className="gantt-zoom-controls">
          <button className="btn btn-sm btn-secondary" onClick={() => setZoom(Math.max(0.5, zoom - 0.5))}>
            <ZoomOut size={14} />
          </button>
          <span style={{ fontSize: 12, color: 'var(--text-muted)', minWidth: 50, textAlign: 'center' }}>
            {zoom === 0.5 ? 'Mes' : zoom === 1 ? 'Semana' : 'Día'}
          </span>
          <button className="btn btn-sm btn-secondary" onClick={() => setZoom(Math.min(2, zoom + 0.5))}>
            <ZoomIn size={14} />
          </button>
          <button className="btn btn-sm btn-primary" onClick={scrollToToday}>
            <Calendar size={14} /> Hoy
          </button>
          <button className="btn btn-sm btn-secondary" onClick={() => scrollTo('left')}>
            <ChevronLeft size={14} />
          </button>
          <button className="btn btn-sm btn-secondary" onClick={() => scrollTo('right')}>
            <ChevronRight size={14} />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="loading-screen"><div className="spinner" /></div>
      ) : !selectedProject ? (
        <div className="empty-state"><FolderKanban size={48} /><h3>Selecciona un proyecto</h3></div>
      ) : rows.length === 0 ? (
        <div className="empty-state"><Calendar size={48} /><h3>Sin tareas ni hitos</h3><p>Añade tareas al proyecto para ver el Gantt</p></div>
      ) : (
        <div className="gantt-container">
          {/* Sidebar - Task names */}
          <div className="gantt-sidebar" style={{ width: SIDEBAR_WIDTH }}>
            <div className="gantt-sidebar-header" style={{ height: HEADER_HEIGHT }}>
              <span>Tarea / Hito</span>
              <span>Estado</span>
            </div>
            <div className="gantt-sidebar-body">
              {rows.map((row, i) => {
                if (row.type === 'task') {
                  const t = row.data;
                  const Icon = STATUS_ICONS[t.status] || Circle;
                  return (
                    <div key={`t-${t.id}`} className="gantt-sidebar-row" style={{ height: ROW_HEIGHT }}>
                      <div className="gantt-task-label">
                        <Icon size={14} style={{ color: STATUS_COLORS[t.status], flexShrink: 0 }} />
                        <span className="gantt-task-name" title={t.name}>{t.name}</span>
                        {t.is_contractual && <Shield size={10} style={{ color: '#F59E0B', flexShrink: 0 }} />}
                      </div>
                      <span className="gantt-task-pct" style={{ color: STATUS_COLORS[t.status] }}>
                        {Math.round(t.progress_percentage || 0)}%
                      </span>
                    </div>
                  );
                } else {
                  const m = row.data;
                  const isOverdue = m.deadline_date && new Date(m.deadline_date) < today && m.status !== 'completed';
                  return (
                    <div key={`m-${m.id}`} className="gantt-sidebar-row milestone-row" style={{ height: ROW_HEIGHT }}>
                      <div className="gantt-task-label">
                        <span className="gantt-milestone-diamond" style={{
                          background: isOverdue ? '#EF4444' : m.status === 'completed' ? '#10B981' : '#F59E0B'
                        }}>◆</span>
                        <span className="gantt-task-name" title={m.name}>{m.name}</span>
                      </div>
                      <span className="gantt-task-pct" style={{ color: isOverdue ? '#EF4444' : '#F59E0B', fontSize: 10 }}>
                        {m.payment_percentage || 0}%
                      </span>
                    </div>
                  );
                }
              })}
            </div>
          </div>

          {/* Timeline */}
          <div className="gantt-timeline" ref={timelineRef}>
            <div className="gantt-timeline-inner" style={{ width: timelineWidth, minHeight: rows.length * ROW_HEIGHT + HEADER_HEIGHT }}>
              {/* Month headers */}
              <div className="gantt-header" style={{ height: HEADER_HEIGHT }}>
                {months.map((m, i) => (
                  <div key={i} className="gantt-month" style={{ left: m.x, width: m.width }}>
                    {m.label}
                  </div>
                ))}
              </div>

              {/* Grid */}
              <div className="gantt-grid" style={{ top: HEADER_HEIGHT }}>
                {/* Week lines */}
                {weekLines.map((x, i) => (
                  <div key={i} className="gantt-week-line" style={{ left: x }} />
                ))}

                {/* Today line */}
                {todayX && todayX > 0 && (
                  <div className="gantt-today-line" style={{ left: todayX }}>
                    <span className="gantt-today-label">Hoy</span>
                  </div>
                )}

                {/* Bars */}
                {rows.map((row, i) => {
                  const y = i * ROW_HEIGHT;

                  if (row.type === 'task') {
                    const t = row.data;
                    const x1 = getX(row.start);
                    const x2 = getX(row.end);
                    if (x1 === null && x2 === null) {
                      return (
                        <div key={`tb-${t.id}`} className="gantt-bar-placeholder" style={{ top: y + 10, left: 10 }}>
                          Sin fechas
                        </div>
                      );
                    }
                    const barX = x1 || (x2 ? x2 - 40 : 0);
                    const barW = (x1 && x2) ? Math.max(x2 - x1, 12) : 40;
                    const color = STATUS_COLORS[t.status];
                    const progress = (t.progress_percentage || 0) / 100;

                    return (
                      <div key={`tb-${t.id}`} className="gantt-bar-wrapper" style={{ top: y + 8, left: barX, width: barW }}>
                        <div className="gantt-bar" style={{ background: color + '30', borderColor: color + '60' }}>
                          <div className="gantt-bar-fill" style={{ width: `${progress * 100}%`, background: color }} />
                        </div>
                        <div className="gantt-bar-tooltip">
                          {t.name} — {Math.round(t.progress_percentage || 0)}%
                        </div>
                      </div>
                    );
                  } else {
                    const m = row.data;
                    const x = getX(row.date);
                    if (x === null) return null;
                    const isOverdue = m.deadline_date && new Date(m.deadline_date) < today && m.status !== 'completed';
                    const color = isOverdue ? '#EF4444' : m.status === 'completed' ? '#10B981' : '#F59E0B';

                    return (
                      <div key={`mb-${m.id}`} className="gantt-milestone-marker" style={{ top: y + 10, left: x - 8 }}>
                        <div className="gantt-diamond" style={{ background: color, boxShadow: `0 0 8px ${color}40` }} />
                        <div className="gantt-bar-tooltip">
                          {m.name} — {m.payment_amount ? `${Number(m.payment_amount).toLocaleString()}€` : ''}
                        </div>
                      </div>
                    );
                  }
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Legend */}
      {rows.length > 0 && (
        <div className="gantt-legend">
          {Object.entries(STATUS_COLORS).map(([key, color]) => (
            <span key={key} className="gantt-legend-item">
              <span style={{ width: 20, height: 6, borderRadius: 3, background: color, display: 'inline-block' }} />
              {STATUS_LABELS[key]}
            </span>
          ))}
          <span className="gantt-legend-item">
            <span style={{ color: '#F59E0B', fontSize: 14 }}>◆</span> Hito contractual
          </span>
          <span className="gantt-legend-item">
            <span style={{ width: 2, height: 14, background: '#F59E0B', display: 'inline-block' }} /> Hoy
          </span>
        </div>
      )}
    </div>
  );
}
